import React, { useEffect, useMemo, useState } from 'react';
import { ContactCard } from './ContactCard';
import { useAccountOverview } from '../../../../../features/account/account-overview/account-overview.hook';
import { ProfileDrawer } from './ProfileDrawer';

import Styles from './style';
import { Button, Dropdown, Empty, Flex, Input, Spin } from 'antd';
import { SearchOutlined, SortDescendingOutlined } from '@ant-design/icons';
const { Container, ContactGroup, EmptyState } = Styles;

export const Stakeholders: React.FC = () => {
  const [loading, setLoading] = React.useState<string[]>([]);
  const [selectedStakeholderId, setSelectedStakeholderId] = useState('');

  const [searchTerm, setSearchTerm] = useState('');

  const { selectedAccount, selectedAccountFetching } = useAccountOverview();

  const [sort, setSort] = useState('email');

  const sortedContacts = [...selectedAccount.contacts];
  sortedContacts.sort((a, b) => {
    if (sort === 'email') {
      return a.email.localeCompare(b.email);
    }

    if (sort === 'name' && a.name && b.name) {
      return a.name.localeCompare(b.name);
    }

    if (sort === 'decision-maker') {
      return a.decisionMakingStatus?.isDecisionMaker === true ? -1 : 1;
    }

    if (sort === 'profile') {
      return !!a.profile ? -1 : 1;
    }

    return 0;
  });

  useEffect(() => {
    if (sortedContacts.length > 0) {
      setSelectedStakeholderId(sortedContacts[0].id);
    }
  }, [selectedAccount.id]);

  useEffect(() => {
    setLoading(
      (selectedAccount.contacts || [])
        .filter(contact => !!contact.isGeneratingProfile)
        .map(contact => contact.id),
    );
  }, [selectedAccount.contacts]);

  const selectedStakeHolder = useMemo(() => {
    return selectedAccount.contacts.find(c => c.id === selectedStakeholderId);
  }, [selectedStakeholderId, selectedAccount.contacts]);

  return (
    <>
      {selectedAccountFetching && sortedContacts.length === 0 && (
        <EmptyState>
          <Spin />
        </EmptyState>
      )}
      {sortedContacts.length === 0 && !selectedAccountFetching && (
        <EmptyState>
          <Empty description="No stakeholders found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </EmptyState>
      )}
      {sortedContacts.length > 0 && (
        <Container>
          <ContactGroup>
            <Flex gap={4}>
              <Input
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search Stakeholders"
                suffix={<SearchOutlined />}
              />

              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                menu={{
                  selectable: true,
                  selectedKeys: [sort],
                  items: [
                    {
                      key: 'reset',
                      label: 'Reset',
                      disabled: sort === 'email',
                      onClick: () => setSort('email'),
                    },
                    {
                      key: 'name',
                      label: 'Name',
                      disabled: sort === 'name',
                      onClick: () => setSort('name'),
                    },
                    {
                      key: 'email',
                      label: 'Email',
                      disabled: sort === 'email',
                      onClick: () => setSort('email'),
                    },
                    {
                      key: 'decision-maker',
                      label: 'Decision Maker',
                      disabled: sort === 'decision-maker',
                      onClick: () => setSort('decision-maker'),
                    },
                    {
                      key: 'profile',
                      label: 'Has Profile',
                      disabled: sort === 'profile',
                      onClick: () => setSort('profile'),
                    },
                  ],
                }}
              >
                <Button
                  style={{ width: 36 }}
                  icon={<SortDescendingOutlined />}
                  color={sort !== 'email' ? 'primary' : 'default'}
                  variant={sort !== 'email' ? 'filled' : 'outlined'}
                />
              </Dropdown>
            </Flex>
            {selectedAccountFetching && (
              <Flex align="center" justify="center" style={{ height: '100%' }}>
                <Spin />
              </Flex>
            )}
            {!selectedAccountFetching &&
              sortedContacts
                .filter(
                  contact =>
                    contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()),
                )
                .map(contact => {
                  const {
                    email,
                    id,
                    name,
                    noShows,
                    talkRatio,
                    totalMeetings,
                    lastMeetingDate,
                    decisionMakingStatus,
                    advocacyClassification,
                  } = contact;
                  const isDecisionMaker =
                    decisionMakingStatus && decisionMakingStatus.isDecisionMaker === true;
                  const advocacyStatus = advocacyClassification
                    ? advocacyClassification.classification
                    : 'None';
                  return (
                    <ContactCard
                      selected={selectedStakeholderId === contact.id}
                      onClick={() => {
                        setSelectedStakeholderId(contact.id);
                      }}
                      isDecisionMaker={isDecisionMaker}
                      advocacyClassification={advocacyStatus}
                      attendees={totalMeetings - noShows}
                      lastMeeting={lastMeetingDate}
                      noShows={noShows}
                      name={name}
                      meetings={totalMeetings}
                      percentage={talkRatio}
                      email={email}
                      hasProfile={!!contact.profile}
                      profileLastGenerated={contact.profileLastGeneratedAt}
                      key={id}
                    />
                  );
                })}
          </ContactGroup>

          {selectedStakeHolder && (
            <ProfileDrawer
              stakeholder={selectedStakeHolder}
              setLoading={(id: string) => setLoading([...loading, id])}
              loading={loading.includes(selectedStakeholderId) || selectedAccountFetching}
            />
          )}
        </Container>
      )}
    </>
  );
};
